import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router";
import {
	Alert,
	AlertContainer,
	AlertContent,
	AlertDescription,
	AlertTitle,
} from "@/components/ui/alert";
import {
	Cake,
	Check,
	ClockAlert,
	Copy,
	Coffee,
	MoveRight,
	Receipt,
	Star,
	PartyPopper,
	Tag,
	Trophy,
	Rocket,
	Baby,
	BellElectric,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useApp } from "@/app/providers/app-provider";

interface Discount {
	code: string;
	title: string;
	icon: React.ReactNode;
	text: React.ReactNode;
	expires: string; // YYYY-MM-DD
	starts: string; // YYYY-MM-DD
}

interface AlertBanner {
	icon: React.ReactNode;
	title: string;
	description: string;
	link?: string;
	expiryDate: string; // YYYY-MM-DD
	startingDate: string; // YYYY-MM-DD
}

const discounts: Discount[] = [
	{
		code: "XUPTFQAS",
		title: "Let's Party!",
		icon: (
			<Cake
				size={24}
				className="block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				It's our birthday! Enter this promotion code below to get an additional{" "}
				<strong className="bolder underline">25% Off for Life</strong>!
			</span>
		),
		starts: "2026-03-12",
		expires: "2026-03-31",
	},
	{
		code: "MEMORIAL2026",
		title: "Turn up the focus!",
		icon: (
			<Star
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				And turn down the distractions this Memorial Day. Use the code below to
				take an additional <strong className="bolder underline">25% off</strong>{" "}
				our Premium services.
			</span>
		),
		starts: "2026-05-25",
		expires: "2026-06-01",
	},
	{
		code: "FATHERSDAY26",
		title: "Honoring All of the Dad's",
		icon: (
			<Star
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				For the dads who do it all—stay in the zone this Father's Day. Take an
				additional <strong className="bolder underline">20% off</strong> our
				Premium sounds.
			</span>
		),
		starts: "2026-06-18",
		expires: "2026-06-28",
	},
	{
		code: "JULY4TH2026",
		title: "Freedom from Distractions",
		icon: (
			<Cake
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				4TH of July SUMMER SALE! 50% off. Our biggest sale of the summer!
			</span>
		),
		starts: "2026-07-01",
		expires: "2026-07-12",
	},
	{
		code: "LABORDAY2026",
		title: "Labor Day Sale - 25% Off",
		icon: (
			<PartyPopper
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				This Labor Day week, treat yourself to distraction-free focus.
			</span>
		),
		starts: "2026-09-07",
		expires: "2026-09-13",
	},
	{
		code: "COFFEEDAY2026",
		title: "National Coffee Day",
		icon: (
			<Coffee
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: <span>FLASH SALE! 50% off Coffitivity Premium - One day only!</span>,
		starts: "2026-09-29",
		expires: "2026-09-29",
	},
	// !! NOTE: Long running sales must come after short terms sales within the same period !!
	{
		code: "BACKTOSCHOOL2026",
		title: "15% Back to school sale!",
		icon: (
			<BellElectric
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				Contact us to receive special discounts for teachers and college
				students.
			</span>
		),
		starts: "2026-09-01",
		expires: "2026-09-30",
	},
	{
		code: "BLACKFRIDAY2025",
		title: "Black Friday Brew Out",
		icon: (
			<Receipt
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				Our biggest sale of the year! Get 50% off now through Cyber Monday.
			</span>
		),
		starts: "2025-11-24",
		expires: "2025-12-01",
	},
	{
		code: "NEWYEAR2026",
		title: "New Year Focus",
		icon: (
			<Tag
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				Start the new year off with a renewed sense of focus, creativity, and
				clarity. <span className="font-bold">50% off now!</span>
			</span>
		),
		starts: "2025-12-31",
		expires: "2026-01-11",
	},
	{
		code: "NEWYEAR2026",
		title: "New Year Focus",
		icon: (
			<Tag
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				Start the new year off with a renewed sense of focus, creativity, and
				clarity. <span className="font-bold">50% off now!</span>
			</span>
		),
		starts: "2025-12-31",
		expires: "2026-01-11",
	},
	{
		code: "PUSH2026",
		title: "Persistence Until Success Happens",
		icon: (
			<Trophy
				size={24}
				className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
			/>
		),
		text: (
			<span>
				You got this! We hope you had a strong start to the year and if you need
				a little pick-me up, here's an additional{" "}
				<span className="font-bold">40% off!</span>
			</span>
		),
		starts: "2026-02-09",
		expires: "2026-02-15",
	},
];

const alerts: AlertBanner[] = [
	{
		title: "Upvote Coffitivity:",
		description:
			"Help us reach #1 on Product Hunt! Vote now and find a 20% off discount code.",
		link: "https://www.producthunt.com/products/coffitivity?launch=coffitivity-expanded-library",
		icon: <Rocket size={24} className="text-primary-foreground" />,
		expiryDate: "2026-01-27",
		startingDate: "2026-01-27",
	},
	{
		title: "We made something new!",
		description: "Celebrate with us and get 25% off for life.",
		link: "the-buzz/we-made-something-new",
		icon: <Baby size={24} className="text-primary-foreground" />,
		expiryDate: "2026-04-30",
		startingDate: "2026-04-01",
	},
];

const SpecialDiscountAlert = ({ discount }: { discount: Discount }) => {
	const [copied, setCopied] = useState(false);

	const copyCode = async () => {
		try {
			await navigator.clipboard.writeText(discount.code);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy code:", err);
		}
	};

	const endDateParts = discount.expires.split("-").map(Number);
	const endDate = new Date(
		endDateParts[0],
		endDateParts[1] - 1, // Month is 0-indexed
		endDateParts[2],
		23, // Set to the end of the day
		59,
		59,
		999, // Include entire end day
	);
	const displayEndDate = endDate.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});

	return (
		<Alert
			variant="primary"
			className="rounded-none bg-primary text-primary-foreground py-2 px-4 leading-3"
		>
			<AlertContainer>
				{discount.icon}
				<AlertContent className="text-sm">
					<Link to="/pricing">
						<AlertTitle className="mr-2 md: ml-0 text-base md:text-md inline md-block text-primary-foreground">
							{discount.title}
						</AlertTitle>
					</Link>
					<div className="flex flex-col gap-1 text-primary-foreground leading-4">
						<Link to="/pricing">
							<p className="">
								{discount.text} Expires {displayEndDate}.
							</p>
						</Link>
						<p className="flex items-center gap-2">
							Code: <span className="font-bold">{discount.code} </span>
							<Button
								variant="ghost"
								title="Copy to clipboard"
								size="sm"
								onClick={copyCode}
								className="h-6 w-6 p-0 hover:bg-gray-200 dark:hover:bg-gray-700"
							>
								{copied ? (
									<Check className="h-3 w-3 text-primary" />
								) : (
									<Copy className="h-3 w-3" />
								)}
							</Button>
						</p>
					</div>
				</AlertContent>
			</AlertContainer>
			<Link to="/pricing">
				<MoveRight
					size={24}
					className="shrink-0 mx-2 text-primary-foreground"
				/>
			</Link>
		</Alert>
	);
};

const AlertBannerComponent = ({ alert }: { alert: AlertBanner }) => {
	return (
		<Link to={alert.link || "/pricing"}>
			<Alert className="rounded-none bg-primary text-primary-foreground py-2 px-4 sm:px-8 md:p-4 leading-3">
				<AlertContainer>
					<div className="hidden md:block ml-2 mr-4 shrink-0">{alert.icon}</div>
					<div>
						<AlertTitle className="mr-2 md: ml-0 text-sm inline text-primary-foreground">
							{alert.title}
						</AlertTitle>
						<AlertDescription className="inline text-sm text-primary-foreground">
							{alert.description}
						</AlertDescription>
					</div>
				</AlertContainer>
				<MoveRight
					size={24}
					className="shrink-0 mx-2 text-primary-foreground"
				/>
			</Alert>
		</Link>
	);
};

const StandardPremiumDiscount = () => {
	return (
		<Link to="/pricing">
			<Alert
				variant="primary"
				className="rounded-none bg-primary text-primary-foreground py-2 px-4 sm:px-8 md:p-4 leading-3"
			>
				<AlertContainer>
					<ClockAlert
						size={24}
						className="hidden md:block ml-2 mr-4 shrink-0 text-primary-foreground"
					/>
					<div>
						<AlertTitle className="mr-2 md: ml-0 text-sm inline text-primary-foreground">
							Heads up!
						</AlertTitle>
						<AlertDescription className="inline text-sm text-primary-foreground">
							You're missing out on our full experience. Get{" "}
							<span className="md:font-bold">40% off</span> with our annual
							plan. <span className="underline">Cancel anytime</span>.
						</AlertDescription>
					</div>
				</AlertContainer>
				<MoveRight
					size={24}
					className="shrink-0 mx-2 text-primary-foreground"
				/>
			</Alert>
		</Link>
	);
};

const GetPremiumAlert = ({ className }: { className?: string }) => {
	const currentDate = new Date(); // This uses the client's local time

	// Check through the alerts to see if any are active (priority 1)
	const activeAlert = alerts.find((alert) => {
		const startDateParts = alert.startingDate.split("-").map(Number);
		const endDateParts = alert.expiryDate.split("-").map(Number);

		// Create start and end dates using local time
		const startDate = new Date(
			startDateParts[0],
			startDateParts[1] - 1,
			startDateParts[2],
		);
		const endDate = new Date(
			endDateParts[0],
			endDateParts[1] - 1,
			endDateParts[2],
			23,
			59,
			59,
			999,
		); // Include entire end day

		return currentDate >= startDate && currentDate <= endDate;
	});

	// Check through the discounts to see if any are active (priority 2)
	const activeDiscount = discounts.find((discount) => {
		const startDateParts = discount.starts.split("-").map(Number);
		const endDateParts = discount.expires.split("-").map(Number);

		// Create start and end dates using local time
		const startDate = new Date(
			startDateParts[0],
			startDateParts[1] - 1,
			startDateParts[2],
		);
		const endDate = new Date(
			endDateParts[0],
			endDateParts[1] - 1,
			endDateParts[2],
			23,
			59,
			59,
			999,
		); // Include entire end day

		return currentDate >= startDate && currentDate <= endDate;
	});

	const location = useLocation();
	const { appStore } = useApp();
	const { isUserSubscribed } = appStore;
	const style = isUserSubscribed ? "hidden" : "w-full";

	const hiddenPages = [
		"/pricing",
		"/checkout-review",
		"/checkout",
		"/terms",
		"/account",
	];
	const hiddenPageStyle = hiddenPages.includes(location.pathname)
		? "hidden"
		: "";

	return (
		<section className={cn(style, hiddenPageStyle, className)}>
			{activeAlert ? (
				<AlertBannerComponent alert={activeAlert} />
			) : activeDiscount ? (
				<SpecialDiscountAlert discount={activeDiscount} />
			) : (
				<StandardPremiumDiscount />
			)}
		</section>
	);
};

export default GetPremiumAlert;
